import { isAppError } from '@/lib/api/errors';

/**
 * How the sign-in form should treat a failed login.
 *
 * `message` is an i18n KEY, not prose — the caller runs it through `t()`. `needsTotp`
 * asks the form to reveal the authenticator field; it is never turned back off, because
 * an account that has MFA does not stop having it between two attempts.
 */
export type LoginErrorTreatment = {
  message:
    | 'auth.rateLimited'
    | 'auth.locked'
    | 'auth.totpRequired'
    | 'auth.invalidTotp'
    | 'auth.invalid';
  needsTotp: boolean;
};

/**
 * Classify a failed `POST /api/v1/admin/auth/login`.
 *
 * This lives outside the component because the mapping is the whole security-visible
 * behaviour of the screen and it was wrong: be-svet answers an MFA-enrolled account in
 * TWO shapes, and reading only one of them locked every enrolled user out of the console.
 *
 *   - password correct, no code sent → **422** `validation_error`, with
 *     `details[0].field === 'totp_code'` (internal/domain/users.Service.Login →
 *     `common.NewValidation("MFA code required.", …)`)
 *   - password correct, code wrong   → **401** `"Invalid MFA code."`
 *   - password wrong                 → **401**, no MFA wording, no `totp_code` detail
 *
 * The 422 is the first thing an enrolled user ever sees, and the old check —
 * `status === 401 && /totp|mfa|…/.test(message)` — did not match it, so the attempt fell
 * through to the catch-all and reported "email or password is incorrect" while never
 * revealing the code field. Keying on the structured `totp_code` detail fixes that and is
 * the more durable channel besides: it survives translation and any rewording of the
 * message. The 401 regex is kept for the wrong-code case, which carries no field detail.
 *
 * FE-001 still holds and is why neither branch is a bare status check: a plain wrong
 * password must surface "email or password is incorrect", never a spurious authenticator
 * prompt.
 */
export function classifyLoginError(err: unknown, totpAlreadyShown: boolean): LoginErrorTreatment {
  if (!isAppError(err)) return { message: 'auth.invalid', needsTotp: totpAlreadyShown };

  if (err.is('rate_limited')) return { message: 'auth.rateLimited', needsTotp: totpAlreadyShown };
  if (err.status === 401 && /lock/i.test(err.message)) {
    return { message: 'auth.locked', needsTotp: totpAlreadyShown };
  }

  // "We still need a code" — including a blank re-submit once the field is showing.
  const totpDetail = (err.details ?? []).some((d) => d.field === 'totp_code');
  if (totpDetail) return { message: 'auth.totpRequired', needsTotp: true };

  // "The code you sent did not verify."
  if (err.status === 401 && /totp|mfa|2fa|authenticat|verification code/i.test(err.message)) {
    return { message: 'auth.invalidTotp', needsTotp: true };
  }

  return { message: 'auth.invalid', needsTotp: totpAlreadyShown };
}
